import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectSettings } from "../app/slices/settingsSlice";
import { useCargosTable } from "../context/CargoTableContext";
import { useAuth } from "../features/auth/AuthContext";
import {
	useAddTimocomRefMutation,
	useAddTransRefMutation,
	useReportChunkCompanionErrorMutation,
	useReportChunkTimocomCompanionErrorMutation,
	useReportChunkTransCompanionErrorMutation,
	useReportTimocomCompanionErrorMutation,
	useReportTransCompanionErrorMutation,
} from "../features/cargo/cargoApiSlice";
import {
	DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS,
	sendCargosToCompanion,
} from "../utils/companion";

export function useAddViaCompanion() {
	const { user } = useAuth();
	const userCompanyId = user?.companyId;

	const hasCustomTransConnection = user?.hasCustomTransConnection;

	const { t } = useTranslation();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const settings = useSelector(selectSettings);
	const companionAppConfigEncrypted = settings?.companionAppConfig;
	const {
		setTransCompanionStatusForCargo,
		setTransCompanionStatusForCargos,
		setTimocomCompanionStatusForCargo,
		setTimocomCompanionStatusForCargos,
	} = useCargosTable();

	const [addTransRef] = useAddTransRefMutation(); // triggered in background so no loading state needed
	const [addTimocomRef] = useAddTimocomRefMutation(); // triggered in background so no loading state needed
	const [reportChunkCompanionError] = useReportChunkCompanionErrorMutation();
	const [reportCompanionErrorTrans] = useReportTransCompanionErrorMutation();
	const [reportCompanionErrorTimocom] =
		useReportTimocomCompanionErrorMutation();
	const [reportChunkCompanionErrorTrans] =
		useReportChunkTransCompanionErrorMutation();
	const [reportChunkCompanionErrorTimocom] =
		useReportChunkTimocomCompanionErrorMutation();

	function addViaCompanion(data, isRetry = false, useTimocomAi = false) {
		// companion app
		// send event to companion app
		const cargosToPostOnTrans = hasCustomTransConnection
			? [] // will be added via API, so no need to send to companion
			: data.trans;
		const cargosToPostOnTimocom = DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS.has(
			userCompanyId,
		)
			? []
			: data.timocom;

		const cargoIdsTrans = cargosToPostOnTrans.map((c) => c.id);
		const cargoIdsTimocom = cargosToPostOnTimocom.map((c) => c.id);

		// If sending fails mid-way, don't report already-successful cargos as failed.
		const successfulCargoIdsTrans = new Set();
		const successfulCargoIdsTimocom = new Set();

		// local overlay so UI can update even before cargos list refreshes
		const statusToSet = isRetry ? "retrying" : "pending";

		setTransCompanionStatusForCargos(cargoIdsTrans, statusToSet);

		setTimocomCompanionStatusForCargos(cargoIdsTimocom, statusToSet);

		// best-effort: if companion isn't running, don't block saving cargos
		void sendCargosToCompanion(
			{
				trans: cargosToPostOnTrans,
				timocom: cargosToPostOnTimocom,
			},
			{
				useTimocomAi,
				userCompanyId,
				companionAppConfigEncrypted,
				onEvent: async (event) => {
					const platforms = {
						"trans.cargoStatusChanged": {
							name: "Trans",
							setStatus: setTransCompanionStatusForCargo,
							addRef: addTransRef,
							reportError: reportCompanionErrorTrans,
							successSet: successfulCargoIdsTrans,
						},
						"timocom.cargoStatusChanged": {
							name: "TC",
							setStatus: setTimocomCompanionStatusForCargo,
							addRef: addTimocomRef,
							reportError: reportCompanionErrorTimocom,
							successSet: successfulCargoIdsTimocom,
						},
						"trans.auth": {
							name: "Trans",
							setStatuses: setTransCompanionStatusForCargos,
							reportErrors: reportChunkCompanionErrorTrans,
							cargosIds: cargoIdsTrans,
						},
						"timocom.auth": {
							name: "TC",
							setStatuses: setTimocomCompanionStatusForCargos,
							reportErrors: reportChunkCompanionErrorTimocom,
							cargosIds: cargoIdsTimocom,
						},
					};

					const platform = platforms[event.type];
					if (!platform) return;

					const { status, cargo, referenceNumber, infoType } = event.payload;
					const cargoId = cargo?.id;
					const cargoUuid = cargo?.uuid;

					const statusMessages = {
						processing: {
							text: t("companion_started_processing", {
								cargoUuid,
								platformName: platform.name,
							}),
							variant: "info",
						},
						error: {
							text: t("companion_adding_error", {
								cargoUuid,
								platformName: platform.name,
							}),
							variant: "error",
						},
						success: {
							text: t("companion_adding_success", {
								cargoUuid,
								platformName: platform.name,
							}),
							variant: "success",
						},
						no_credentials: {
							text: t("companion_no_credentials", {
								platformName: platform.name,
							}),
							variant: "warning",
						},
						wrong_credentials: {
							text: t("companion_wrong_credentials", {
								platformName: platform.name,
							}),
							variant: "warning",
						},
						info: {
							variant: "info",
							types: {
								captcha_detected: t("companion_captcha_detected", {
									platformName: platform.name,
								}),
								two_factor_auth_detected: t(
									"companion_two_factor_auth_detected",
									{
										platformName: platform.name,
									},
								),
								address_not_found: t("companion_address_not_found", {
									platformName: platform.name,
								}),
								country_not_found: t("companion_country_not_found", {
									platformName: platform.name,
								}),
							},
						},
					};

					if (status === "undefined" && cargoId != null) {
						platform.setStatus(cargoId, status);
					}

					const message = statusMessages[status];
					if (!message) return;

					const messageText =
						status === "info" ? message.types[infoType] : message.text;
					enqueueSnackbar(messageText, {
						variant: message.variant,
						persist: status === "info",
						action: (key) => {
							if (status !== "info") return null;

							return (
								<IconButton
									onClick={() => closeSnackbar(key)}
									aria-label="close"
									color="primary"
									size="small"
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							);
						},
					});

					if (
						cargoId != null &&
						![
							"success",
							"error",
							"no_credentials",
							"wrong_credentials",
						].includes(status)
					) {
						platform.setStatus(cargoId, status);
					}

					if (status === "success" && cargoId != null) {
						try {
							await platform
								.addRef({
									cargoId,
									referenceNumber,
									type: "add",
								})
								.unwrap();

							platform.successSet.add(cargoId);
							platform.setStatus(cargoId, "success");
						} catch (err) {
							console.error(
								`Failed to save ${platform.name} reference number for cargo.`,
								err,
							);

							Sentry.captureMessage(
								`Failed to link ${platform.name} cargo (${referenceNumber}) with system cargo (${cargoId})`,
								"error",
								{
									extra: {
										cargoId,
										referenceNumber,
										platform: platform.name,
									},
								},
							);

							enqueueSnackbar(
								t("companion_link_error", {
									platformName: platform.name,
								}),
								{ variant: "error" },
							);
						}
					}

					if (status === "error" && cargoId != null) {
						platform.setStatus(cargoId, "error");

						try {
							await platform
								.reportError({
									cargoId,
									type: "add",
								})
								.unwrap();
						} catch (err) {
							console.error(
								`Failed to report ${platform.name} post error for cargo.`,
								err,
							);

							Sentry.captureMessage(
								`Failed to report ${platform.name} post error for cargo ${cargoId}`,
								"error",
								{
									extra: {
										cargoId,
										platform: platform.name,
									},
								},
							);

							enqueueSnackbar(
								t("companion_report_error_error", {
									platformName: platform.name,
								}),
								{ variant: "error" },
							);
						}
					}

					if (status === "no_credentials" || status === "wrong_credentials") {
						platform.setStatuses(platform.cargosIds, "error");

						try {
							await platform
								.reportErrors({
									cargoIds: platform.cargosIds,
									type: "add",
								})
								.unwrap();
						} catch (err) {
							console.error(
								`Failed to report ${platform.name} ${status} error for cargos.`,
								err,
							);

							Sentry.captureMessage(
								`Failed to report ${platform.name} ${status} error for cargos ${platform.cargosIds.join(",")}`,
								"error",
								{
									extra: {
										cargoIds: platform.cargosIds,
										platform: platform.name,
									},
								},
							);

							enqueueSnackbar(
								t("companion_report_error_error", {
									platformName: platform.name,
								}),
								{ variant: "error" },
							);
						}
					}
				},
			},
		)
			.then((ack) => {
				const count =
					ack && typeof ack === "object" && "count" in ack
						? ack.count
						: cargosToPostOnTrans.length + cargosToPostOnTimocom.length;

				const wasAborted = ack.results.some(
					(result) => result.automation.aborted !== undefined,
				);
				const abortedCount = wasAborted
					? ack.results.reduce((sum, result) => {
							return (
								sum +
								(result.automation.aborted === undefined ? 0 : result.count)
							);
						}, 0)
					: 0;

				const finalCount = wasAborted ? count - abortedCount : count;

				if (finalCount > 0) {
					enqueueSnackbar(
						t("companion_finished_adding", { count: finalCount }),
						{
							variant: "success",
						},
					);
				}
			})
			.catch(async (_error) => {
				const message = t("companion_top_level_error");
				enqueueSnackbar(message, {
					variant: "error",
				});

				// report to backend that cargos were not posted, so they can be posted later when companion is running
				const cargoIdsTransToReport = cargoIdsTrans.filter(
					(id) => !successfulCargoIdsTrans.has(id),
				);
				const cargoIdsTimocomToReport = cargoIdsTimocom.filter(
					(id) => !successfulCargoIdsTimocom.has(id),
				);

				if (
					cargoIdsTransToReport.length === 0 &&
					cargoIdsTimocomToReport.length === 0
				) {
					return;
				}

				setTransCompanionStatusForCargos(cargoIdsTransToReport, "error");
				setTimocomCompanionStatusForCargos(cargoIdsTimocomToReport, "error");

				try {
					await reportChunkCompanionError({
						cargoIdsTrans: cargoIdsTransToReport,
						cargoIdsTimocom: cargoIdsTimocomToReport,
						type: "add",
					}).unwrap();
				} catch (error) {
					const reportMessage =
						error instanceof Error
							? error.message
							: "Failed to report post error to backend.";

					enqueueSnackbar(t("companion_failed_mass_report"), {
						variant: "error",
					});
					console.error(
						"Failed to report post error to backend.",
						reportMessage,
					);
					Sentry.captureMessage(
						"Failed to report companion post error to backend",
						"error",
						{
							extra: {
								cargoIdsTrans: cargoIdsTransToReport,
								cargoIdsTimocom: cargoIdsTimocomToReport,
								reportMessage: reportMessage,
							},
						},
					);
				}
			});
	}

	return { addViaCompanion };
}
